import React, { useEffect, useRef, useState, useContext } from "react";
import qapi from "views_shared/app/javascript/api";
import SectionCard from "../../profiles/SectionCard";
import CompanySkeletonPage from "../search/company_skeleton_page";
import CompanyCard from "../card";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";
import { CompanyContext } from "../data/company_context_management";

// The client companies a PR agency represents, as a self-contained section card: it owns its
// <SectionCard> so it can collapse away entirely when there is nothing to show. Viewers never get an
// empty card; an editor keeps it (with a one-line empty state) because the "Add a section" dropdown
// only lists sections that are switched OFF — a switched-on but empty section would otherwise leave
// its own show/hide switch unreachable.
function CompanyBrands() {
  const { companyId, company, viewerContext, setOpenEditModal } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const configured = company.displayType === "pr_firm" && !!company.page_config?.show_brands;

  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [brands, setBrands] = useState([]);
  const [asyncError, setAsyncError] = useState();

  // The provider hands out a fresh `company` object on every authoritative read, so its identity --
  // not the company id alone -- is this section's refresh signal. It restarts pagination from an
  // empty list, so the rows on screen can never be a mix of pages fetched before and after the
  // refresh, and it is a dependency of the fetch below, so a section already sitting on page 1
  // refetches too. Taking the new signal during the render that first sees it, rather than in an
  // effect, keeps a refresh to a single request. Dropping `hasMore` matters as much as the rows:
  // it takes the old scroll trigger out of the page, so a trigger sitting in the viewport cannot
  // advance pagination off the pre-refresh list while page 1 is still in flight.
  const [fetchContext, setFetchContext] = useState({ companyId, company });
  if (fetchContext.companyId !== companyId || fetchContext.company !== company) {
    setFetchContext({ companyId, company });
    setBrands([]);
    setHasMore(false);
    setPage(1);
    // Straight to the loading skeleton: without this the commit that empties the list would paint
    // one frame of the empty state (or collapse the card entirely for a viewer) before the fetch
    // effect gets to run.
    setIsLoading(true);
  }

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  // Effect to track infinite scrolling intersection observer event, which increments page. Each
  // trigger is consumed exactly once, and only while there is more to fetch: the observer can
  // still deliver a queued intersection for a trigger node a refresh just unmounted, and paging
  // forward off that would append later pages onto a list that is being refetched from page 1.
  const consumedTriggerRef = useRef(0);
  useEffect(() => {
    if (lastTriggeredAt <= consumedTriggerRef.current) return;

    consumedTriggerRef.current = lastTriggeredAt;
    if (hasMore) setPage((prevPage) => prevPage + 1);
  }, [lastTriggeredAt, hasMore]);

  // Switching the section off takes the scroll trigger below out of the page while the infinite
  // scroll observer is still watching the old node, and the observer only re-attaches when
  // `hasMore` changes. Emptying the section stops pagination, so switching it back on fetches page
  // 1 afresh and flips `hasMore` back on against the new trigger.
  useEffect(() => {
    if (configured) return;

    setBrands([]);
    setHasMore(false);
    setPage(1);
  }, [configured]);

  // Retrieve a PR agencies list of represented brands (client companies) from the API and track the page number
  useEffect(() => {
    let componentIsMounted = true;
    const fetchBrands = async () => {
      try {
        if (companyId && configured) {
          if (page === 1) setIsLoading(true);

          const options = {
            page,
          };

          const response = await qapi.companyRepresentations.getAllByCompanyId(companyId, options);
          if (componentIsMounted && response.data) {
            const transformedBrands = response.data.map((item) => ({
              id: item.promoted_company_id,
              name: item.promoted_company_name,
              profile_image_url: item.promoted_company_profile_image_url,
              slug: item.promoted_company_slug,
              migrate_id: item.promoted_company_migrate_id,
            }));
            // Page 1 replaces instead of appending: it is also what a restart re-fetches, and
            // appending there would stack a second copy of the first page onto the rows on screen.
            setBrands((prevRecords) => (page === 1 ? transformedBrands : [...prevRecords, ...transformedBrands]));
            setHasMore(response.remainingRows > 0);
          }
          if (componentIsMounted) setIsLoading(false);
        }
      } catch (error) {
        if (componentIsMounted) setAsyncError(error);
      }
    };
    fetchBrands();

    // Cleanup function to avoid the "Can't perform a React state update on an unmounted component"/memory leak problem
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, configured, page, fetchContext]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  if (!configured) return null;

  const isEmpty = !isLoading && brands.length === 0;
  if (isEmpty && !editable) return null;

  return (
    <SectionCard
      id="company-section-brands"
      title="Our Brands"
      editable={editable}
      onEdit={() => setOpenEditModal("BRANDS")}
      editButtonId="modal-button-BRANDS"
    >
      {isEmpty ? (
        <div className="text-muted">No brands added yet.</div>
      ) : (
        <div className="col px-0">
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3">
            {isLoading && <CompanySkeletonPage numberOfCards={12} />}
            {!isLoading &&
              brands.map((brand) => (
                <div className="col mb-3" key={brand.id}>
                  <CompanyCard
                    slug={brand.slug}
                    name={brand.name}
                    profileImageUrl={brand.profile_image_url}
                    id={brand.id}
                    migrateId={brand.migrate_id}
                  />
                </div>
              ))}
          </div>
          {hasMore && (
            <div ref={scrollTriggerRef} className="row row-cols-1 row-cols-md-2 row-cols-xl-3">
              <CompanySkeletonPage numberOfCards={3} singleRow />
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

export default CompanyBrands;
